import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import { Footer } from '../components/site/FooterCTA';

const LAST_UPDATED = '20 February 2026';

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-serif text-[26px] leading-tight" style={{ color: 'var(--yb-ink)' }}>
      {title}
    </h2>
    <div className="mt-3 space-y-3 text-[15px] leading-relaxed" style={{ color: 'var(--yb-text)' }}>
      {children}
    </div>
  </section>
);

const Privacy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy · YouBuild';
  }, []);
  return (
    <div style={{ backgroundColor: 'var(--yb-bg)' }}>
      <Navbar />
      <main className="pt-28 pb-24" data-testid="privacy-page">
        <div className="max-w-[760px] mx-auto px-6">
          <p className="text-[12.5px] uppercase tracking-widest" style={{ color: 'var(--yb-muted)' }}>
            Legal
          </p>
          <h1 className="font-serif text-[42px] sm:text-[54px] leading-[1.05] tracking-[-0.02em] mt-3" style={{ color: 'var(--yb-ink)' }}>
            Privacy Policy
          </h1>
          <p className="mt-3 text-[13px]" style={{ color: 'var(--yb-muted)' }}>
            Last updated: {LAST_UPDATED}
          </p>

          <Section title="Who we are">
            <p>
              YouBuild is a website builder for Australian tradies and small businesses. It helps
              you describe your business in plain English and publish a working site with a quote
              form. This policy explains what personal information we collect when you use the
              service at <strong>you-build.app</strong>, how we use it, and the choices you have.
            </p>
          </Section>

          <Section title="What we collect">
            <p>
              <strong>Account details.</strong> When you sign up we store your email address and a
              hashed password (or an OAuth identifier if you sign in with a provider). We do not
              store your plain password.
            </p>
            <p>
              <strong>Project content.</strong> The prompts you enter, the HTML we generate for
              you, any photos you upload, and any notes you attach to your projects are stored so
              you can come back and edit them.
            </p>
            <p>
              <strong>Lead submissions on your sites.</strong> When a visitor fills in a quote form
              on a site you published with YouBuild, we store their submission (typically name,
              phone, suburb, message) so it lands in your Leads inbox. The visitor's IP address is
              temporarily recorded for spam prevention.
            </p>
            <p>
              <strong>Payment details.</strong> Subscriptions are processed by Stripe. We never see
              your card number. Stripe returns a customer identifier which we store so we can look
              up your plan status.
            </p>
            <p>
              <strong>Usage logs.</strong> Standard technical logs, request timing, error traces,
              and basic device information, kept for a short period for diagnostics.
            </p>
          </Section>

          <Section title="How we use it">
            <p>
              We use your information to run the service, to deliver the pages you generate, to
              route quote-form submissions to you (including an email notification when you have
              email notifications configured), to prevent abuse, and to comply with our legal
              obligations. We do not sell your personal information, and we do not use your
              project content to train third-party models.
            </p>
          </Section>

          <Section title="Third parties we rely on">
            <p>
              We use a small set of trusted providers to run YouBuild: Supabase (authentication,
              database, file storage), MongoDB Atlas (application data), Anthropic (AI generation),
              Unsplash (fallback stock imagery), Stripe (payments), and Resend (transactional
              email). Each processes only the data it needs to perform its role.
            </p>
          </Section>

          <Section title="Where your data lives">
            <p>
              Data is hosted with reputable providers in cloud regions selected for reliability.
              Some processing may occur outside Australia. By using YouBuild you consent to that
              cross-border processing.
            </p>
          </Section>

          <Section title="Your choices">
            <p>
              You can update your account details in the app, delete any project you own (which
              removes its content, uploaded photos, and quote submissions), or ask us to delete
              your account entirely. To make an access, correction, or deletion request under the
              Australian Privacy Principles, email us at{' '}
              <a href="mailto:ginacastela62@gmail.com" style={{ color: 'var(--yb-indigo)' }}>ginacastela62@gmail.com</a>.
            </p>
          </Section>

          <Section title="Cookies">
            <p>
              We use a small number of cookies and similar local-storage items strictly to keep
              you logged in and to remember basic preferences. We do not use tracking cookies for
              advertising.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>
              If we make material changes we will update the date at the top of this page and, for
              significant changes, tell you in-app or by email.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions or requests: <a href="mailto:ginacastela62@gmail.com" style={{ color: 'var(--yb-indigo)' }}>ginacastela62@gmail.com</a>.
            </p>
            <p>
              <Link to="/" style={{ color: 'var(--yb-indigo)' }}>← Back to YouBuild</Link>
            </p>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
